import React, { useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  DESIGN_COLLAPSED_TABS_INSET_VAR,
  DESIGN_RIGHT_PANEL_WIDTH_VAR,
} from '../design-right-panel-resize.hook';
import { Tooltip } from '@ui/Tooltip';
import View2dIcon from '@ui/Icon/icons/view-2d.svg?react';
import View2dRingIcon from '@ui/Icon/icons/view-2d-ring.svg?react';

export type ViewMode = '2d' | '3d';

type ViewModeToggleProps = {
  mode: ViewMode;
  onSwitchView: (mode: ViewMode) => void;
  /** Map bearing in degrees; the ring counter-rotates so its arrow keeps north. */
  bearing?: number;
};

/**
 * Top-right Design map control: the plan mark toggles the camera.
 * Clicking switches to 2D, or back to 3D when already in plan view.
 */
export const ViewModeToggle: React.FC<ViewModeToggleProps> = ({
  mode,
  onSwitchView,
  bearing = 0,
}) => {
  const { classes } = useStyles();

  // Mapbox bearing is the compass direction shown "up"; normalise for display.
  const normalizedBearing = ((Math.round(bearing) % 360) + 360) % 360;
  const viewpoint = `View from ${getViewpointCompassPoint(normalizedBearing)}`;
  // Only promise the reset while the map is actually turned away from north,
  // and keep it on its own centered line below the viewpoint.
  const viewLabel = normalizedBearing ? (
    <span className={classes.tooltipLabel}>
      {viewpoint}
      <br />
      (Reset to north)
    </span>
  ) : (
    viewpoint
  );

  const handleToggle = useCallback(
    () => onSwitchView(mode === '2d' ? '3d' : '2d'),
    [mode, onSwitchView],
  );

  return (
    <Tooltip title={viewLabel} placement="left">
      <button
        type="button"
        className={classes.root}
        onClick={handleToggle}
        aria-label={mode === '2d' ? 'Switch to 3D view' : 'Switch to 2D view'}
      >
        <View2dIcon className={classes.mark} />
        <View2dRingIcon
          className={classes.ring}
          style={{ transform: `translate(-50%, -50%) rotate(${-bearing}deg)` }}
        />
      </button>
    </Tooltip>
  );
};

/** Compass points, clockwise from north in 45 degree steps. */
const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/**
 * Compass point the camera looks *from*, as the nearest of eight directions.
 *
 * The bearing is the direction shown "up" on screen, and the camera sits
 * opposite it: with north up (0°) the viewer stands to the south looking north,
 * so the tooltip reads "View from S".
 * @param normalizedBearing - Bearing in degrees, 0-359
 */
const getViewpointCompassPoint = (normalizedBearing: number): string => {
  const viewpointBearing = (normalizedBearing + 180) % 360;
  return (
    COMPASS_POINTS[Math.round(viewpointBearing / 45) % COMPASS_POINTS.length] ??
    'N'
  );
};

const useStyles = makeStyles()(() => ({
  tooltipLabel: {
    display: 'block',
    textAlign: 'center',
  },
  root: {
    // Bare control: only the mark itself is visible on the map.
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0,
    lineHeight: 0,
    cursor: 'pointer',
    opacity: 0.7,
    transition: 'opacity 0.15s ease',
    '&:hover': {
      opacity: 1,
    },
    // The mark sits in normal flow; the ring stacks on top of it.
    display: 'block',
    position: 'absolute',
    right: `calc(16px + var(${DESIGN_RIGHT_PANEL_WIDTH_VAR}, 0px) + var(${DESIGN_COLLAPSED_TABS_INSET_VAR}, 0px))`,
    top: 14,
    zIndex: 1,
  },
  mark: {
    display: 'block',
    width: 32,
    height: 32,
  },
  ring: {
    // Larger than the mark but sharing its centre point.
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: 48,
    height: 48,
  },
}));
